import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import _ from "lodash";

interface UseSyncedListParamsOptions {
    storagePrefix: string;
    urlKeys?: {
        searchParams?: string[];
        /** URL param keys that are NOT filter params (e.g. pagination). Default: ["page", "pageSize"] */
        nonFilterKeys?: string[];
    };
    defaultQueryFields: string[];
    /** Allowed search field values. Any stored/URL field not in this list is stripped. */
    validQueryFields?: string[];
    /**
     * One-time migration gate for the persisted search fields. Bump this number to force every user
     * whose stored version is lower to discard their persisted `fields` (keeping `query`) and fall
     * back to `defaultQueryFields`. Used to roll out new backend default search fields.
     */
    searchParamsVersion?: number;
    /**
     * One-time migration gate for the persisted filter params. Bump this number to force every user
     * whose stored version is lower to discard their persisted filters (localStorage AND URL) and
     * fall back to `defaultFilterParams`. Used when the backend renames/removes filter enum values
     * that may still be persisted client-side.
     */
    filterParamsVersion?: number;
    defaultFilterParams?: Record<string, any>;
}

export const useSyncedListParams = ({
    storagePrefix,
    urlKeys,
    defaultQueryFields,
    validQueryFields,
    searchParamsVersion,
    filterParamsVersion,
    defaultFilterParams = {},
}: UseSyncedListParamsOptions) => {
    const [urlParams, setUrlParams] = useSearchParams();

    const searchParamKeys = urlKeys?.searchParams ?? ["query", "fields"];
    const nonFilterKeys = urlKeys?.nonFilterKeys ?? ["page", "pageSize"];
    const allExcludeKeys = [...searchParamKeys, ...nonFilterKeys];

    const STORAGE_KEYS = {
        searchParams: `${storagePrefix}_searchParams`,
        searchParamsVersion: `${storagePrefix}_searchParamsVersion`,
        filterParams: `${storagePrefix}_filterParams`,
        filterParamsVersion: `${storagePrefix}_filterParamsVersion`,
    };

    // One-time search-fields migration: true when the consumer bumped `searchParamsVersion` past the
    // version last stored for this prefix. Evaluated once (first render). While true, any persisted
    // `fields` — whether from localStorage OR the URL — are discarded in favour of `defaultQueryFields`
    // so the downstream TDA reconciliation can apply the current backend default search fields.
    const migrationNeededRef = useRef<boolean>(
        searchParamsVersion != null &&
            Number(localStorage.getItem(STORAGE_KEYS.searchParamsVersion) ?? 0) < searchParamsVersion
    );

    // Same one-time gate for filter params: while true, persisted filters (localStorage AND URL) are
    // discarded in favour of `defaultFilterParams`, because they may carry enum values the backend
    // no longer accepts and a single stale value fails the whole list query.
    const filterMigrationNeededRef = useRef<boolean>(
        filterParamsVersion != null &&
            Number(localStorage.getItem(STORAGE_KEYS.filterParamsVersion) ?? 0) < filterParamsVersion
    );

    const hasAnyUrlParam = (keys: string[]) => keys.some((key) => urlParams.has(key));

    // Strip search fields that are no longer valid (e.g. renamed/removed enum values).
    // Falls back to the defaults when nothing valid remains. This also cleans up stale
    // values previously persisted to localStorage on the next render (via the persist effect).
    const sanitizeFields = (fields: string[]): string[] => {
        if (!validQueryFields) return fields;
        const filtered = fields.filter((f) => validQueryFields.includes(f));
        return filtered.length ? filtered : defaultQueryFields;
    };

    /** Returns true if the URL contains any param that is not a search or pagination param */
    const hasAnyFilterParam = () =>
        Array.from(urlParams.entries()).some(([key]) => !allExcludeKeys.includes(key));

    // Resolve the active search fields from a raw source, but force the defaults while a migration
    // is pending so stale persisted fields (localStorage or URL) don't win.
    const resolveFields = (raw: string[] | undefined): string[] =>
        sanitizeFields(migrationNeededRef.current ? defaultQueryFields : (raw ?? defaultQueryFields));

    const getInitialSearchParams = () => {
        if (hasAnyUrlParam(searchParamKeys)) {
            return {
                query: urlParams.get("query") || "",
                fields: resolveFields(urlParams.getAll("fields")),
            };
        }
        // Widget navigation: filter params present but no search params → start fresh
        if (hasAnyFilterParam()) {
            return { query: "", fields: sanitizeFields(defaultQueryFields) };
        }
        const local = localStorage.getItem(STORAGE_KEYS.searchParams);
        if (local) {
            const parsed = JSON.parse(local);
            return { query: parsed.query ?? "", fields: resolveFields(parsed.fields) };
        }
        return { query: "", fields: sanitizeFields(defaultQueryFields) };
    };

    const tryParseJsonObject = (s: string): any => {
        if (typeof s !== "string") return s;
        const trimmed = s.trim();
        if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return s;
        try {
            return JSON.parse(trimmed);
        } catch {
            return s;
        }
    };

    const getInitialFilterParams = () => {
        if (filterMigrationNeededRef.current) {
            return { ...defaultFilterParams };
        }
        if (hasAnyFilterParam()) {
            const params: Record<string, any> = {};
            // Group multi-value params as arrays, single-value as plain string
            const seen = new Set<string>();
            Array.from(urlParams.entries()).forEach(([key]) => {
                if (allExcludeKeys.includes(key) || seen.has(key)) return;
                seen.add(key);
                const values = urlParams.getAll(key);
                params[key] = values.length === 1 ? tryParseJsonObject(values[0]) : values;
            });
            return params;
        }
        // Widget navigation: search params present but no filter params → start fresh
        if (hasAnyUrlParam(searchParamKeys)) {
            return { ...defaultFilterParams };
        }
        const local = localStorage.getItem(STORAGE_KEYS.filterParams);
        return local ? JSON.parse(local) : { ...defaultFilterParams };
    };

    const [searchParams, setSearchParams] = useState(getInitialSearchParams);
    const [filterParams, setFilterParams] = useState(getInitialFilterParams);

    // Sync state from URL only when the URL actually carries the relevant params.
    // Filter/search state is also managed by direct set calls in UI handlers,
    // so the effect must not wipe state just because the URL is silent about it
    // (e.g. after a pagination-only URL change).
    useEffect(() => {
        if (hasAnyFilterParam() && !filterMigrationNeededRef.current) {
            const nextFilter: Record<string, any> = {};
            const seen = new Set<string>();
            Array.from(urlParams.entries()).forEach(([key]) => {
                if (allExcludeKeys.includes(key) || seen.has(key)) return;
                seen.add(key);
                const values = urlParams.getAll(key);
                nextFilter[key] = values.length === 1 ? tryParseJsonObject(values[0]) : values;
            });
            // Keep the previous object when nothing actually changed: every URL write (e.g. the
            // pagination sync) re-runs this effect, and a fresh-but-equal object would ripple
            // through consumers as a real change — refetches, pagination resets, chip re-layout.
            setFilterParams((prev: Record<string, any>) =>
                _.isEqual(prev, nextFilter) ? prev : nextFilter
            );
        }

        if (hasAnyUrlParam(searchParamKeys)) {
            const nextSearch = {
                query: urlParams.get("query") || "",
                fields: resolveFields(urlParams.getAll("fields")),
            };
            setSearchParams((prev: { query: string; fields: string[] }) =>
                _.isEqual(prev, nextSearch) ? prev : nextSearch
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlParams]);

    // Apply the search-params migration exactly once: strip any stale `fields` from the URL (so the
    // URL-sync effect can't re-apply them) and record the new version so it never re-triggers.
    useEffect(() => {
        if (searchParamsVersion == null) return;
        if (migrationNeededRef.current && urlParams.has("fields")) {
            urlParams.delete("fields");
            setUrlParams(urlParams, { replace: true });
        }
        localStorage.setItem(STORAGE_KEYS.searchParamsVersion, String(searchParamsVersion));
        migrationNeededRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Apply the filter-params migration exactly once: drop the persisted filters and strip every
    // filter key from the URL (so the URL-sync effect can't re-apply them), then record the new
    // version so it never re-triggers.
    useEffect(() => {
        if (filterParamsVersion == null) return;
        if (filterMigrationNeededRef.current) {
            localStorage.removeItem(STORAGE_KEYS.filterParams);
            const staleKeys = Array.from(urlParams.keys()).filter((key) => !allExcludeKeys.includes(key));
            if (staleKeys.length) {
                staleKeys.forEach((key) => urlParams.delete(key));
                setUrlParams(urlParams, { replace: true });
            }
        }
        localStorage.setItem(STORAGE_KEYS.filterParamsVersion, String(filterParamsVersion));
        filterMigrationNeededRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.searchParams, JSON.stringify(searchParams));
    }, [searchParams]);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.filterParams, JSON.stringify(filterParams));
    }, [filterParams]);

    const resetFilterParams = () => {
        const keysToDelete = Array.from(
            new Set(
                Array.from(urlParams.keys()).filter((key) => !allExcludeKeys.includes(key))
            )
        );
        keysToDelete.forEach((key) => urlParams.delete(key));
        setUrlParams(urlParams, { replace: true });
        localStorage.removeItem(STORAGE_KEYS.filterParams);
        setFilterParams({ ...defaultFilterParams });
    };

    return {
        urlParams,
        setUrlParams,
        searchParams,
        setSearchParams,
        filterParams,
        setFilterParams,
        resetFilterParams,
    };
};
